import type { TFunction } from 'i18next';
import {
  healthDocuments,
  workouts,
  type HealthDocumentAnalysisData,
  type SyncedFitFilesPayload,
  type WorkoutImportItem,
  type WorkoutUploadResult,
} from '../services/api';

export type CalendarDropFileKind = 'fit' | 'wse' | 'tcx' | 'health_document' | 'unsupported';

export type CalendarUploadPhase = 'uploading' | 'processing' | 'health_analyzing';

/** Live upload state for full-page overlay (FIT/WSE/TCX/health on calendar). */

export interface CalendarUploadProgress {
  phase: CalendarUploadPhase;
  fileName?: string;
  fileIndex?: number;
  fileTotal?: number;
  uploadPercent?: number;
  processingIndex?: number;
  processingTotal?: number;
}

export const CALENDAR_DROP_MAX_FILES = 100;
/** Upload in small batches so total multipart stays under nginx/PHP body limits. */
export const CALENDAR_UPLOAD_BATCH_SIZE = 5;
const POLL_MS = 2000;
const MAX_POLL_ATTEMPTS = 90;

/** Classify a dropped/uploaded file by extension first, then MIME type. */

export function classifyCalendarDropFile(file: File): CalendarDropFileKind {
  const name = file.name.toLowerCase().trim();
  const ext = name.includes('.') ? name.split('.').pop() ?? '' : '';
  if (ext === 'fit') return 'fit';
  if (ext === 'wse') return 'wse';
  if (ext === 'tcx') return 'tcx';
  if (['pdf', 'jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'].includes(ext)) {
    return 'health_document';
  }
  const mime = (file.type || '').toLowerCase();
  if (mime === 'application/pdf' || mime.startsWith('image/')) {
    return 'health_document';
  }
  return 'unsupported';
}

/** True when the drag originates from the OS file manager (not an in-app workout drag). */

export function isOsFileDrag(e: React.DragEvent | DragEvent): boolean {
  const types = Array.from(e.dataTransfer?.types ?? []);
  // Chromium uses "Files"; some hosts also expose the FileList only on drop.
  if (types.includes('Files') || types.includes('files')) return true;
  return (e.dataTransfer?.files?.length ?? 0) > 0;
}

/** Attribute on day cells for hit-testing under a full-calendar file-drop overlay. */
export const CALENDAR_DATE_ATTR = 'data-calendar-date';

/** Resolve Gregorian YYYY-MM-DD under the pointer (temporarily disables overlay hit-testing). */
export function hitTestCalendarDate(
  clientX: number,
  clientY: number,
  overlayEl?: HTMLElement | null,
): string | null {
  const prev = overlayEl?.style.pointerEvents;
  if (overlayEl) overlayEl.style.pointerEvents = 'none';
  const under = document.elementFromPoint(clientX, clientY);
  if (overlayEl) overlayEl.style.pointerEvents = prev && prev.length > 0 ? prev : 'auto';
  const day = under?.closest?.(`[${CALENDAR_DATE_ATTR}]`) as HTMLElement | null;
  const value = day?.getAttribute(CALENDAR_DATE_ATTR)?.trim() ?? '';
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

export function chunkArray<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

function normalizeImportFilename(filename: string): string {
  const trimmed = filename.trim().replace(/\\/g, '/');
  return trimmed.split('/').pop()?.toLowerCase() ?? trimmed.toLowerCase();
}

/** Minimal import row for resolving workout calendar day after upload. */

export interface ActivityImportSnapshot {
  filename: string;
  startTime?: number | null;
}

function snapshotFromImportItem(item: WorkoutImportItem): ActivityImportSnapshot {
  return {
    filename: item.filename ?? item.normalized_filename ?? '',
    startTime: item.start_time,
  };
}

/** One success row per normalized filename — avoids double-count when API returns processed + queued. */

function dedupeActivityImportSnapshots(
  snapshots: ActivityImportSnapshot[],
): ActivityImportSnapshot[] {
  const seen = new Set<string>();
  const unique: ActivityImportSnapshot[] = [];
  for (const snapshot of snapshots) {
    const key = normalizeImportFilename(snapshot.filename);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(snapshot);
  }
  return unique;
}

function formatGregorianIso(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Resolve workout day from import metadata (start_time or YYYY-MM-DD in filename). */

export function gregorianDateFromActivityImport(snapshot: ActivityImportSnapshot): string | null {
  const startTime = Number(snapshot.startTime);
  if (Number.isFinite(startTime) && startTime > 0) {
    const date = new Date(startTime > 1_000_000_000_000 ? startTime : startTime * 1000);
    return formatGregorianIso(date.getFullYear(), date.getMonth() + 1, date.getDate());
  }
  const match = snapshot.filename.match(/(20\d{2})[-_](\d{2})[-_](\d{2})/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }
  return null;
}

/**
 * Pick which calendar day to focus after upload.
 * - One file or all imports on the same day → that workout day
 * - Multiple files on different days → drop day (testDateHint) or keep current (null)
 * - Health-only on a day cell → testDateHint when metrics saved
 */

export function resolveCalendarFocusDateAfterImport(args: {
  activityFilesSubmitted: number;
  activityImports: ActivityImportSnapshot[];
  testDateHint?: string | null;
  healthImported?: boolean;
  healthOnly?: boolean;
  /** LLM-detected test date from a completed health-document analysis. */
  healthTestDate?: string | null;
}): string | null {
  const { activityImports, testDateHint, healthImported, healthOnly, healthTestDate } = args;
  if (healthOnly && healthImported) {
    return testDateHint ?? healthTestDate ?? null;
  }
  const workoutDates = activityImports
    .map(gregorianDateFromActivityImport)
    .filter((d): d is string => Boolean(d));
  const uniqueDates = [...new Set(workoutDates)];
  if (uniqueDates.length === 1) {
    return uniqueDates[0];
  }
  if (uniqueDates.length > 1) {
    return testDateHint ?? null;
  }
  if (healthImported) {
    return testDateHint ?? healthTestDate ?? null;
  }
  return null;
}

function extractSyncedItems(payload?: SyncedFitFilesPayload): WorkoutImportItem[] {
  if (!payload) return [];
  return payload.items ?? payload.imports ?? [];
}

function extractUploadResult(payload: unknown): WorkoutUploadResult | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const root = payload as { data?: WorkoutUploadResult };
  return root.data ?? (payload as WorkoutUploadResult);
}

function collectImportsNeedingPoll(
  uploadResult: WorkoutUploadResult | undefined,
  batchFiles: File[],
): string[] {
  const pending = new Set<string>();
  const processedKeys = new Set<string>();
  const imports = uploadResult?.imports ?? [];
  for (const imp of imports) {
    const key = normalizeImportFilename(
      imp.normalized_filename ?? imp.filename ?? '',
    );
    if (!key) continue;
    if (imp.status === 'failed') continue;
    if (imp.status === 'processed' && imp.workout_id) {
      processedKeys.add(key);
      continue;
    }
    pending.add(key);
  }
  for (const name of uploadResult?.queued ?? []) {
    const key = normalizeImportFilename(name);
    if (key && !processedKeys.has(key)) pending.add(key);
  }
  // Fallback: poll by uploaded filenames when API omits import rows.
  if (pending.size === 0 && (uploadResult?.queued_count ?? 0) > 0) {
    for (const file of batchFiles) {
      const key = normalizeImportFilename(file.name);
      if (key && !processedKeys.has(key)) pending.add(key);
    }
  }
  return [...pending];
}
async function sleep(ms: number): Promise<void> {
  await new Promise(resolve => setTimeout(resolve, ms));
}

/** Poll workout_imports via synced-fit-files until processed or failed. */

export async function pollWorkoutImports(
  userId: number,
  normalizedFilenames: string[],
  onProgress?: (progress: CalendarUploadProgress) => void,
): Promise<{
  succeeded: ActivityImportSnapshot[];
  failed: Array<{ filename: string; error: string }>;
}> {
  const pending = new Set(
    normalizedFilenames.map(normalizeImportFilename).filter(Boolean),
  );
  const succeeded: ActivityImportSnapshot[] = [];
  const failed: Array<{ filename: string; error: string }> = [];
  const total = pending.size;
  if (total === 0) {
    return { succeeded, failed };
  }
  // Large imports need more queue time without polling the full history more often.
  const pollInterval = total > 20 ? 5000 : POLL_MS;
  const maxAttempts = total > 20 ? 180 : MAX_POLL_ATTEMPTS;
  for (let attempt = 0; attempt < maxAttempts && pending.size > 0; attempt++) {
    onProgress?.({
      phase: 'processing',
      processingIndex: total - pending.size + 1,
      processingTotal: total,
    });
    const res = await workouts.getSyncedFitFiles(userId);
    const lookup = new Map<string, WorkoutImportItem>();
    for (const item of extractSyncedItems(res.data?.data)) {
      const key = normalizeImportFilename(
        item.normalized_filename ?? item.filename ?? '',
      );
      if (key) lookup.set(key, item);
    }
    for (const key of [...pending]) {
      const match = lookup.get(key);
      if (!match) continue;
      if (match.status === 'processed' && match.workout_id) {
        pending.delete(key);
        succeeded.push(snapshotFromImportItem(match));
        continue;
      }
      if (match.status === 'failed') {
        pending.delete(key);
        failed.push({
          filename: match.filename ?? key,
          error: match.error_message ?? 'import_failed',
        });
      }
    }
    if (pending.size > 0) {
      await sleep(pollInterval);
    }
  }
  for (const key of pending) {
    failed.push({ filename: key, error: 'import_poll_timeout' });
  }
  return { succeeded, failed };
}

/** Poll health-document analysis until terminal status or timeout. */

export async function pollHealthDocumentAnalysis(
  analysisId: number,
  onProgress?: (fileName: string) => void,
  fileName?: string,
): Promise<HealthDocumentAnalysisData> {
  for (let i = 0; i < 300; i++) {
    onProgress?.(fileName ?? '');
    await sleep(POLL_MS);
    const res = await healthDocuments.getStatus(analysisId);
    const data = res.data.data;
    if (!data) continue;
    if (['completed', 'partial', 'failed'].includes(data.status)) {
      return data;
    }
  }
  throw new Error('health_document_poll_timeout');
}

export interface CalendarFileDropResult {
  fitCount: number;
  wseCount: number;
  tcxCount: number;
  /** Workouts successfully imported (after server processing). */
  activityImportedCount: number;
  activityFailed: Array<{ filename: string; error: string }>;
  healthSavedCount: number;
  healthPartialCount: number;
  healthFailed: string[];
  unsupported: string[];
  /** Gregorian YYYY-MM-DD to focus calendar; null = keep current selection */
  focusGregorianDate: string | null;
  /** True when a second overlapping upload was ignored (duplicate drop handler). */
  skippedDuplicate?: boolean;
}

export interface CalendarFileDropOptions {
  userId: number;
  /**
   * Gregorian YYYY-MM-DD for health-document metrics when dropping on a calendar day.
   * Omit or null → LLM reads test_date from the document; FIT/WSE/TCX always use file timestamp.
   */
  testDateHint?: string | null;
  locale?: string;
  onProgress?: (progress: CalendarUploadProgress) => void;
  /** @deprecated Prefer onProgress phase health_analyzing */
  onHealthProgress?: (fileName: string) => void;
}

let calendarDayUploadInFlight = false;

/**
 * Upload dropped calendar files: FIT/WSE/TCX → workout import (poll until processed); PDF/image → health-document AI.
 */

export async function uploadCalendarDayFiles(
  files: File[],
  options: CalendarFileDropOptions,
): Promise<CalendarFileDropResult> {
  if (calendarDayUploadInFlight) {
    return {
      fitCount: 0,
      wseCount: 0,
      tcxCount: 0,
      activityImportedCount: 0,
      activityFailed: [],
      healthSavedCount: 0,
      healthPartialCount: 0,
      healthFailed: [],
      unsupported: [],
      focusGregorianDate: null,
      skippedDuplicate: true,
    };
  }
  calendarDayUploadInFlight = true;
  try {
  const result: CalendarFileDropResult = {
    fitCount: 0,
    wseCount: 0,
    tcxCount: 0,
    activityImportedCount: 0,
    activityFailed: [],
    healthSavedCount: 0,
    healthPartialCount: 0,
    healthFailed: [],
    unsupported: [],
    focusGregorianDate: null,
  };
  const fitFiles: File[] = [];
  const wseFiles: File[] = [];
  const tcxFiles: File[] = [];
  const healthFiles: File[] = [];
  let healthImportedTestDate: string | null = null;
  for (const file of files) {
    const kind = classifyCalendarDropFile(file);
    if (kind === 'fit') fitFiles.push(file);
    else if (kind === 'wse') wseFiles.push(file);
    else if (kind === 'tcx') tcxFiles.push(file);
    else if (kind === 'health_document') healthFiles.push(file);
    else result.unsupported.push(file.name);
  }
  const activityTotal = fitFiles.length + wseFiles.length + tcxFiles.length;
  if (activityTotal > CALENDAR_DROP_MAX_FILES) {
    throw new Error('too_many_activity_files');
  }
  const pendingPollKeys: string[] = [];
  const activityImportSnapshots: ActivityImportSnapshot[] = [];
  let uploadedSoFar = 0;
  const totalActivityFiles = activityTotal;
  const uploadActivityBatch = async (batch: File[], kind: 'fit' | 'wse' | 'tcx') => {
    const blobs = batch.map(f => ({ name: f.name, data: f as Blob }));
    const uploadFn = kind === 'fit'
      ? workouts.uploadFitFiles
      : kind === 'wse'
        ? workouts.uploadWseFiles
        : workouts.uploadTcxFiles;
    const batchStartIndex = uploadedSoFar;
    options.onProgress?.({
      phase: 'uploading',
      fileIndex: Math.min(batchStartIndex + batch.length, totalActivityFiles),
      fileTotal: totalActivityFiles,
      fileName: batch.length === 1 ? batch[0].name : undefined,
      uploadPercent: 0,
    });
    const res = await uploadFn(blobs, options.userId, pct => {
      options.onProgress?.({
        phase: 'uploading',
        fileIndex: Math.min(batchStartIndex + batch.length, totalActivityFiles),
        fileTotal: totalActivityFiles,
        fileName: batch.length === 1 ? batch[0].name : undefined,
        uploadPercent: pct,
      });
    });
    uploadedSoFar += batch.length;
    const uploadResult = extractUploadResult(res.data);
    for (const row of uploadResult?.failed ?? []) {
      result.activityFailed.push({
        filename: row.filename,
        error: row.error,
      });
    }
    for (const imp of uploadResult?.imports ?? []) {
      if (imp.status === 'processed' && imp.workout_id) {
        activityImportSnapshots.push(snapshotFromImportItem(imp));
      }
    }
    pendingPollKeys.push(...collectImportsNeedingPoll(uploadResult, batch));
  };
  for (const batch of chunkArray(fitFiles, CALENDAR_UPLOAD_BATCH_SIZE)) {
    await uploadActivityBatch(batch, 'fit');
    result.fitCount += batch.length;
  }
  for (const batch of chunkArray(wseFiles, CALENDAR_UPLOAD_BATCH_SIZE)) {
    await uploadActivityBatch(batch, 'wse');
    result.wseCount += batch.length;
  }
  for (const batch of chunkArray(tcxFiles, CALENDAR_UPLOAD_BATCH_SIZE)) {
    await uploadActivityBatch(batch, 'tcx');
    result.tcxCount += batch.length;
  }
  if (pendingPollKeys.length > 0) {
    const uniquePending = [...new Set(pendingPollKeys.map(normalizeImportFilename))];
    const pollResult = await pollWorkoutImports(
      options.userId,
      uniquePending,
      options.onProgress,
    );
    activityImportSnapshots.push(...pollResult.succeeded);
    result.activityFailed.push(...pollResult.failed);
  }
  const uniqueActivityImports = dedupeActivityImportSnapshots(activityImportSnapshots);
  result.activityImportedCount = uniqueActivityImports.length;
  for (const file of healthFiles) {
    options.onProgress?.({
      phase: 'health_analyzing',
      fileName: file.name,
    });
    options.onHealthProgress?.(file.name);
    try {
      const analyzeOpts: {
        user_id: number;
        test_date_hint?: string;
        locale?: string;
      } = {
        user_id: options.userId,
        locale: options.locale,
      };
      if (options.testDateHint) {
        analyzeOpts.test_date_hint = options.testDateHint;
      }
      const uploadRes = await healthDocuments.analyze(file, analyzeOpts);
      const analysisId = uploadRes.data.data?.id;
      if (!analysisId) {
        result.healthFailed.push(file.name);
        continue;
      }
      const analysis = await pollHealthDocumentAnalysis(
        analysisId,
        name => options.onHealthProgress?.(name),
        file.name,
      );
      if (analysis.status === 'failed') {
        result.healthFailed.push(
          analysis.error_message
            ? `${file.name}: ${analysis.error_message}`
            : file.name,
        );
        continue;
      }
      if (analysis.test_date) {
        healthImportedTestDate = analysis.test_date;
      }
      const saved = analysis.saved_metrics?.length ?? 0;
      if (analysis.status === 'partial') {
        result.healthPartialCount += saved;
      } else {
        result.healthSavedCount += saved;
      }
    } catch {
      result.healthFailed.push(file.name);
    }
  }
  result.focusGregorianDate = resolveCalendarFocusDateAfterImport({
    activityFilesSubmitted: activityTotal,
    activityImports: uniqueActivityImports,
    testDateHint: options.testDateHint,
    healthImported: result.healthSavedCount + result.healthPartialCount > 0,
    healthOnly: activityTotal === 0 && healthFiles.length > 0,
    healthTestDate: healthImportedTestDate,
  });
  return result;
  } finally {
    calendarDayUploadInFlight = false;
  }
}

/** Show toasts for a calendar file-drop result (web / bridge / PWA). */

export function toastCalendarFileDropResult(
  result: CalendarFileDropResult,
  t: TFunction,
  pushToast: (message: string, variant?: 'success' | 'error' | 'info') => void,
): void {
  if (result.skippedDuplicate) {
    pushToast(t('calendar.toast.uploadBusy'), 'info');
    return;
  }
  if (result.activityImportedCount > 0) {
    pushToast(
      t('calendar.toast.importSuccess', {
        count: result.activityImportedCount,
        plural: result.activityImportedCount !== 1 ? 's' : '',
      }),
      'success',
    );
  }
  for (const row of result.activityFailed) {
    const msg =
      row.error === 'import_poll_timeout'
        ? t('calendar.toast.importTimeout', { name: row.filename })
        : t('calendar.toast.importFileFailed', { name: row.filename });
    pushToast(msg, 'error');
  }
  if (result.healthSavedCount > 0) {
    pushToast(
      t('healthDocument.toast.saved', { count: result.healthSavedCount }),
      'success',
    );
  }
  if (result.healthPartialCount > 0) {
    pushToast(
      t('healthDocument.toast.partial', { count: result.healthPartialCount }),
      'info',
    );
  }
  for (const name of result.healthFailed) {
    pushToast(t('calendar.toast.healthDocFailed', { name }), 'error');
  }
  for (const name of result.unsupported) {
    pushToast(t('calendar.toast.unsupportedDropFile', { name }), 'error');
  }

  const hadAnyFeedback =
    result.activityImportedCount > 0 ||
    result.activityFailed.length > 0 ||
    result.healthSavedCount > 0 ||
    result.healthPartialCount > 0 ||
    result.healthFailed.length > 0 ||
    result.unsupported.length > 0;

  if (!hadAnyFeedback) {
    pushToast(t('calendar.toast.uploadNothing'), 'info');
  }
}
